import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  QrCode, Camera, CheckCircle2, AlertCircle, Search, Users,
  UserCheck, X, RotateCcw, Loader2, Sparkles, Home
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { CheckInReport } from './CheckInReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

const QR_REGION_ID = 'rojo-qr-scanner-region';

const CheckIn = () => {
  const [scannerActive, setScannerActive] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [stats, setStats] = useState({ total_expected: 0, checked_in: 0, recent: [] });
  const [attendees, setAttendees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [search, setSearch] = useState('');
  const [processing, setProcessing] = useState(false);
  const scannerRef = useRef(null);
  const lastScannedRef = useRef({ id: null, at: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/check-in/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch {
      // silent
    }
  }, []);

  const fetchAttendees = useCallback(async () => {
    try {
      const [res, roomsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/attendees`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/rooms`, { headers: getAuthHeaders() }),
      ]);
      setAttendees(res.data);
      setRooms(roomsRes.data);
    } catch {
      toast.error('Failed to load attendees');
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchAttendees();
  }, [fetchStats, fetchAttendees]);

  const extractAttendeeId = (text) => {
    // Legacy support: bare attendee id (UUID-style)
    if (/^[a-f0-9-]{20,}$/i.test(text.trim())) return text.trim();
    return null;
  };

  const checkInByToken = useCallback(async (token, opts = {}) => {
    if (processing) return;
    setProcessing(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/check-in/scan`,
        { token },
        { headers: getAuthHeaders() }
      );
      const data = res.data;
      setLastResult({
        ok: true,
        already: data.already_checked_in,
        attendee: data.attendee,
        at: new Date().toISOString(),
      });
      if (data.already_checked_in) {
        toast.warning(`${data.attendee.first_name} was already checked in`);
      } else {
        toast.success(`${data.attendee.first_name} ${data.attendee.last_name} checked in`);
      }
      fetchStats();
      fetchAttendees();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Not a valid ROJO ticket';
      setLastResult({ ok: false, error: detail, raw: opts.raw, at: new Date().toISOString() });
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  }, [processing, fetchStats, fetchAttendees]);

  const checkInAttendee = useCallback(async (attendeeId, opts = {}) => {
    if (processing) return;
    setProcessing(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/attendees/${attendeeId}/check-in`,
        {},
        { headers: getAuthHeaders() }
      );
      const data = res.data;
      setLastResult({
        ok: true,
        already: data.already_checked_in,
        attendee: data.attendee,
        at: new Date().toISOString(),
      });
      if (data.already_checked_in) {
        toast.warning(`${data.attendee.first_name} was already checked in`);
      } else {
        toast.success(`${data.attendee.first_name} ${data.attendee.last_name} checked in`);
      }
      fetchStats();
      fetchAttendees();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Check-in failed';
      setLastResult({ ok: false, error: detail, raw: opts.raw, at: new Date().toISOString() });
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  }, [processing, fetchStats, fetchAttendees]);

  const handleScan = useCallback((decodedText) => {
    // Debounce: ignore same code within 4 seconds
    const now = Date.now();
    if (lastScannedRef.current.id === decodedText && now - lastScannedRef.current.at < 4000) return;
    lastScannedRef.current = { id: decodedText, at: now };

    // Preferred path: signed ROJO ticket token
    if (decodedText.startsWith('ROJO-TICKET:')) {
      checkInByToken(decodedText, { raw: decodedText });
      return;
    }

    // Legacy fallback: bare attendee id
    const attendeeId = extractAttendeeId(decodedText);
    if (attendeeId) {
      checkInAttendee(attendeeId, { raw: decodedText });
      return;
    }

    setLastResult({ ok: false, error: 'Not a valid ROJO ticket code', raw: decodedText, at: new Date().toISOString() });
    toast.error('Not a valid ROJO ticket');
  }, [checkInByToken, checkInAttendee]);

  const startScanner = useCallback(async () => {
    setLastResult(null);
    setScannerActive(true);
    try {
      const html5QrCode = new Html5Qrcode(QR_REGION_ID);
      scannerRef.current = html5QrCode;
      await html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        handleScan,
        () => { /* ignore decode-per-frame errors */ }
      );
    } catch (err) {
      console.error('Scanner start failed', err);
      toast.error('Could not access camera. Check permissions.');
      setScannerActive(false);
      scannerRef.current = null;
    }
  }, [handleScan]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch {
        // ignore
      }
      scannerRef.current = null;
    }
    setScannerActive(false);
  }, []);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const undoCheckIn = async (attendeeId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/retreat/attendees/${attendeeId}/undo-check-in`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Check-in cleared');
      setLastResult(null);
      fetchStats();
      fetchAttendees();
    } catch {
      toast.error('Could not undo check-in');
    }
  };

  const filteredAttendees = attendees.filter(a => {
    if (!search.trim()) return false;
    const q = search.toLowerCase();
    return (
      a.first_name?.toLowerCase().includes(q) ||
      a.last_name?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q)
    );
  }).slice(0, 8);

  const pctChecked = stats.total_expected > 0
    ? Math.round((stats.checked_in / stats.total_expected) * 100)
    : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="check-in-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Check-In</h1>
        <p className="text-gray-600 mt-1">Scan QR codes or look up attendees by name for fast arrival processing</p>
      </div>

      {/* Live stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-sm" data-testid="stats-checked-in">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Checked In</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.checked_in}</p>
                <p className="text-xs text-gray-500 mt-0.5">of {stats.total_expected} expected</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <UserCheck className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                style={{ width: `${pctChecked}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="stats-remaining">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Still to Arrive</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{Math.max(0, stats.total_expected - stats.checked_in)}</p>
                <p className="text-xs text-gray-500 mt-0.5">{pctChecked}% checked in</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="stats-recent">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-2">Last to Arrive</p>
            {stats.recent.length === 0 ? (
              <p className="text-sm text-gray-400 mt-2">No check-ins yet</p>
            ) : (
              <div className="space-y-1.5">
                {stats.recent.slice(0, 3).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700 truncate">{r.first_name} {r.last_name}</span>
                    <span className="text-xs text-gray-400 shrink-0 ml-2">
                      {new Date(r.checked_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scanner */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <QrCode className="w-4 h-4 text-gray-400" />
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">QR Scanner</p>
              </div>
              {scannerActive && (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />
                  Scanning
                </Badge>
              )}
            </div>

            {/* Camera viewport */}
            <div className="relative aspect-square bg-gray-900 rounded-xl overflow-hidden mb-4">
              <div id={QR_REGION_ID} className="absolute inset-0 [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover" />
              {!scannerActive && (
                <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                  <div>
                    <Camera className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-sm text-gray-300">Camera off</p>
                    <p className="text-xs text-gray-500 mt-1">Tap Start to scan tickets</p>
                  </div>
                </div>
              )}
              {scannerActive && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-12 border-2 border-white/40 rounded-lg" />
                  <div className="absolute top-12 left-12 w-6 h-6 border-t-2 border-l-2 border-emerald-400 rounded-tl-lg" />
                  <div className="absolute top-12 right-12 w-6 h-6 border-t-2 border-r-2 border-emerald-400 rounded-tr-lg" />
                  <div className="absolute bottom-12 left-12 w-6 h-6 border-b-2 border-l-2 border-emerald-400 rounded-bl-lg" />
                  <div className="absolute bottom-12 right-12 w-6 h-6 border-b-2 border-r-2 border-emerald-400 rounded-br-lg" />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {!scannerActive ? (
                <Button
                  onClick={startScanner}
                  className="flex-1 bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600"
                  data-testid="start-scanner-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Start Scanner
                </Button>
              ) : (
                <Button
                  onClick={stopScanner}
                  variant="outline"
                  className="flex-1"
                  data-testid="stop-scanner-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Stop
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center">
              Point the camera at an attendee's ticket QR code
            </p>
          </CardContent>
        </Card>

        {/* Right panel: last result + manual search */}
        <div className="space-y-6">
          {/* Last result */}
          {lastResult && (
            <Card
              className={`border-0 shadow-sm overflow-hidden ${
                lastResult.ok && !lastResult.already ? 'ring-2 ring-emerald-200' :
                lastResult.already ? 'ring-2 ring-amber-200' : 'ring-2 ring-red-200'
              }`}
              data-testid="check-in-result"
            >
              <CardContent className="p-5">
                {lastResult.ok ? (
                  <div>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full shrink-0 ${
                        lastResult.already ? 'bg-amber-100' : 'bg-emerald-100'
                      }`}>
                        {lastResult.already ? (
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                        ) : (
                          <Sparkles className="w-5 h-5 text-emerald-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
                          {lastResult.already ? 'Already Checked In' : 'Welcome'}
                        </p>
                        <h3 className="text-xl font-bold text-gray-900 mt-1">
                          {lastResult.attendee.first_name} {lastResult.attendee.last_name}
                        </h3>
                        <p className="text-sm text-gray-500">{lastResult.attendee.email}</p>
                        {lastResult.attendee.room_name && (
                          <div className="mt-3 flex items-center gap-2 text-sm">
                            <Home className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-700">{lastResult.attendee.room_name}</span>
                            {lastResult.attendee.bed_assignment && (
                              <span className="text-gray-500">· {lastResult.attendee.bed_assignment}</span>
                            )}
                          </div>
                        )}
                        <Button
                          onClick={() => undoCheckIn(lastResult.attendee.id)}
                          variant="ghost"
                          size="sm"
                          className="mt-3 text-xs text-gray-500 hover:text-gray-900 -ml-2"
                          data-testid="undo-check-in-btn"
                        >
                          <RotateCcw className="w-3 h-3 mr-1.5" />
                          Undo check-in
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-red-100 shrink-0">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Not Found</p>
                      <p className="text-sm text-gray-700 mt-1">{lastResult.error}</p>
                      {lastResult.raw && (
                        <p className="text-xs text-gray-400 mt-2 font-mono break-all">{lastResult.raw}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Manual lookup */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-gray-400" />
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Manual Check-In</p>
              </div>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                data-testid="check-in-search"
              />
              {search.trim() && (
                <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto">
                  {filteredAttendees.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No matches</p>
                  ) : filteredAttendees.map(a => {
                    const isCheckedIn = !!a.checked_in_at;
                    return (
                      <div
                        key={a.id}
                        className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`attendee-row-${a.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {a.first_name} {a.last_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{a.email}</p>
                        </div>
                        {isCheckedIn ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs shrink-0 ml-2">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            In
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => checkInAttendee(a.id)}
                            disabled={processing}
                            className="ml-2 h-7 px-2.5 text-xs bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600"
                            data-testid={`check-in-btn-${a.id}`}
                          >
                            {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Check In'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <CheckInReport attendees={attendees} rooms={rooms} />
    </div>
  );
};

export default CheckIn;
